/* eslint-disable no-unsafe-optional-chaining */
import { cloneElement, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { setShowReportTooltips } from '../redux/actions/ReportActions';

function Portal({ children }) {
  return createPortal(children, document.body);
}

function getPoint(element, tooltipRef, space) {
  const rect = element?.getBoundingClientRect();
  return {
    x: rect?.left + (element?.offsetWidth - tooltipRef.offsetWidth) / 2 || 0,
    y: rect?.top - (tooltipRef.offsetHeight + space) || 0,
  };
}

/**
 * `Tooltip` component for displaying hover tooltips using React Portals.
 *
 * Renders tooltips outside the regular DOM hierarchy to avoid overflow and clipping issues,
 * ensuring that the tooltip is always fully visible.
 *
 * This component uses React Redux to manage tooltip visibility state across the application.
 *
 * Note:
 * - Accessibility considerations must be taken into account to ensure that the content rendered
 *   via Portals is still fully accessible, especially when it comes to managing focus and screen
 *   reader interactions.
 * - This tooltip renders a cloned version of the children passed in. This could potentially lead
 *   to unexpected behaviour. See https://react.dev/reference/react/cloneElement for details.
 * - By adding a tooltip, any ref prop on children will be overridden.
 * - Tooltip will not be properly read out by screen readers if it is within react-data-grid. These
 *   labels will have to be handled manually.
 *
 * @param {object} props
 * @param {React.ReactNode} props.content Content to be displayed inside the tooltip.
 * @param {number} [props.space=7] Amount of pixels between the tooltip and the target element.
 * @param {boolean} [props.active=false] If true, the tooltip will be always visible.
 * @param {boolean} [props.disabled=false] If true, disables the tooltip.
 * @param {React.ReactNode} props.children The target element that the tooltip is attached to.
 *
 */
function Tooltip({ content, space = 7, active = true, disabled, children }) {
  const [show, setShow] = useState(false);
  const posRef = useRef({ x: 0, y: 0 });
  const tooltipRef = useRef();
  const targetRef = useRef();

  // TODO: To generate the ID, DirectPay Partner Portal uses useID() hook which is not available
  // in React version 17. At the moment, the static ID works fine but
  // should be updated when the react is updated.
  const tooltipId = 'tooltip';

  // used to prevent tooltip from remaining active on scroll in grid
  // TODO: following should be initialized using showReportTooltips property
  // of the reports object in redux.
  const showReportTooltips = true;
  const dispatch = useDispatch();

  function handleActivate(e) {
    setShow(true);
    dispatch(setShowReportTooltips(true));
    posRef.current = getPoint(e.currentTarget, tooltipRef.current, space);
  }

  function handleDeactivate() {
    setShow(false);
  }

  useEffect(() => {
    if (active) {
      handleActivate({ currentTarget: targetRef.current });
    } else {
      handleDeactivate({ currentTarget: targetRef.current });
    }
  }, [active]); // eslint-disable-line

  useEffect(() => {
    // to remove tooltips from showing on scroll and becoming misaligned
    const clearTooltips = () => dispatch(setShowReportTooltips(false));
    window.addEventListener('scroll', clearTooltips);
    return () => {
      window.removeEventListener('scroll', clearTooltips);
    };
  }, []); // eslint-disable-line

  return (
    <>
      {cloneElement(children, {
        onMouseOver: handleActivate,
        onMouseOut: handleDeactivate,
        onFocus: handleActivate,
        onBlur: handleDeactivate,
        'aria-describedby': disabled ? undefined : tooltipId,
        ref: targetRef,
      })}
      <Portal>
        <span
          style={{ top: posRef.current.y, left: posRef.current.x }}
          className={`tooltipText ${show && showReportTooltips && !disabled ? 'tooltipVisible' : ''}`}
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
        >
          {content}
        </span>
      </Portal>
    </>
  );
}

export default Tooltip;
